import { MY_ESTIMATE_REQUEST_STATUS_LABELS, type MyEstimateGroup } from '@massilia/queries'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  StatusBadge,
} from '@massilia/ui'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

import { REQUEST_BADGE_ICONS, REQUEST_BADGE_TONES } from './estimate-request-summary'
import { type EstimateResponseActions, EstimateResponseCard } from './estimate-response-card'
import { RequestStatusNotice } from './request-status-notice'

interface EstimateRequestDrawerProps {
  downloadingResponseId: null | string
  buildActions: (responseId: string) => EstimateResponseActions | null
  /** `null` = fermé. La demande est relue à chaque rendu depuis la liste. */
  group: MyEstimateGroup | null
  onOpenChange: (open: boolean) => void
}

const formatDay = (iso: string) => format(new Date(iso), 'd MMMM yyyy', { locale: fr })

/**
 * Le détail d'une demande : ses propositions, et les gestes ouverts dessus.
 *
 * Un panneau plutôt qu'un dépliement dans la liste — comparer deux propositions
 * demande de les avoir toutes sous les yeux, sans le reste des demandes autour.
 * Pleine largeur en dessous de `sm`, où un panneau étroit ne servirait à rien.
 */
export function EstimateRequestDrawer({
  downloadingResponseId,
  buildActions,
  group,
  onOpenChange,
}: EstimateRequestDrawerProps) {
  const BadgeIcon = group ? REQUEST_BADGE_ICONS[group.status] : undefined

  return (
    <Sheet onOpenChange={onOpenChange} open={group !== null}>
      {/* Deux précautions pour le bouton de fermeture, toutes deux apprises à
          ses dépens :
          - `[&>button]:z-20`, sinon il passe DERRIÈRE l'en-tête collant ;
          - le défilement est porté par le bloc INTÉRIEUR, jamais par
            `SheetContent`. La croix y est positionnée en `absolute` : dans un
            conteneur qui défile, elle défile avec le contenu et sortait de
            l'écran. Sur téléphone il ne restait alors aucune sortie — pas
            d'overlay à côté (panneau pleine largeur), pas de touche Échap. */}
      <SheetContent className="w-full gap-0 p-0 sm:max-w-xl [&>button]:z-20" side="right">
        {group && (
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {/* Collant : le client fait défiler plusieurs propositions et doit
                garder sous les yeux de quelle demande il s'agit. */}
            <SheetHeader className="sticky top-0 z-10 gap-2 border-b border-gray-light-200 bg-gray-light-50 p-4 pr-12 md:p-6 md:pr-14">
              <SheetTitle className="text-base break-words">{group.route}</SheetTitle>
              <SheetDescription className="text-xs">
                {group.type === 'FLIGHT' ? 'Vol' : 'Séjour'} · {group.passengerCount} voyageur
                {group.passengerCount > 1 ? 's' : ''}
                {group.travelDates.departure && (
                  <> · départ le {formatDay(group.travelDates.departure)}</>
                )}
                {' · '}demandé le {formatDay(group.createdAt)}
              </SheetDescription>
              <div>
                <StatusBadge
                  appearance={REQUEST_BADGE_TONES[group.status].appearance}
                  icon={BadgeIcon ? <BadgeIcon aria-hidden /> : undefined}
                  tone={REQUEST_BADGE_TONES[group.status].tone}
                >
                  {MY_ESTIMATE_REQUEST_STATUS_LABELS[group.status]}
                </StatusBadge>
              </div>
            </SheetHeader>

            <div className="flex flex-col gap-4 p-4 md:p-6">
              {/* L'état de la demande en clair : ce que l'agence fait, et ce
                  qu'on attend du client. Le badge de l'en-tête dit l'état en
                  deux mots, il ne dit pas à qui est le tour. */}
              <RequestStatusNotice group={group} />

              {group.responses.length > 0 && (
                <div className="overflow-hidden rounded-2xl border border-gray-light-200">
                  {group.responses.map((response) => (
                    <EstimateResponseCard
                      key={response.id}
                      actions={buildActions(response.id)}
                      isDownloadingPdf={downloadingResponseId === response.id}
                      response={response}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
